import React, { useState, useEffect } from 'react';
import {
  Download,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './AuditLogViewer.css';

interface AuditLogData {
  _id: string;
  action: string;
  resource: string;
  resourceId?: string;
  oldValue?: unknown;
  newValue?: unknown;
  user: string;
  userIp?: string;
  userAgent?: string;
  timestamp: Date;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

const ACTIONS = [
  { value: 'all', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'read', label: 'Read' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'sync_fingerprint', label: 'Sync fingerprint' },
  { value: 'bulk_delete', label: 'Bulk delete' },
  { value: 'bulk_sync', label: 'Bulk sync' },
];

const RESOURCES = [
  { value: 'all', label: 'All resources' },
  { value: 'pricing_plan', label: 'Pricing plan' },
  { value: 'payment_settings', label: 'Payment settings' },
  { value: 'website_content', label: 'Website content' },
  { value: 'user', label: 'User' },
  { value: 'blog', label: 'Blog' },
  { value: 'fingerprint_record', label: 'Fingerprint' },
];

const AuditLogViewer: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<string>('all');
  const [selectedSuccess, setSelectedSuccess] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;
  const [selectedLog, setSelectedLog] = useState<AuditLogData | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, selectedAction, selectedResource, selectedSuccess, dateRange.start, dateRange.end]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await websiteApi.getAuditLogs({
        page: currentPage,
        limit: itemsPerPage,
        searchTerm: searchTerm || undefined,
        action: selectedAction !== 'all' ? selectedAction : undefined,
        resource: selectedResource !== 'all' ? selectedResource : undefined,
        success: selectedSuccess !== 'all' ? selectedSuccess === 'true' : undefined,
        startDate: dateRange.start || undefined,
        endDate: dateRange.end || undefined,
      });
      setLogs(response.logs);
      setTotalCount(response.totalCount || 0);
      setTotalPages(Math.max(1, Math.ceil((response.totalCount || 0) / itemsPerPage)));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await websiteApi.getAuditLogs({ limit: 10000 });
      const allLogs = response.logs;
      const csvHeaders = ['Timestamp', 'Action', 'Resource', 'Resource ID', 'User', 'IP', 'Success', 'Error'].join(',');
      const csvRows = allLogs.map((log: AuditLogData) => [
        new Date(log.timestamp).toISOString(),
        log.action,
        log.resource,
        log.resourceId || '',
        log.user,
        log.userIp || '',
        log.success ? 'Yes' : 'No',
        log.errorMessage || '',
      ].map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','));
      const blob = new Blob([[csvHeaders, ...csvRows].join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `website-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const getActionClass = (action: string) => {
    switch (action?.toLowerCase()) {
      case 'create': return 'wal-action wal-action--create';
      case 'update': return 'wal-action wal-action--update';
      case 'delete': return 'wal-action wal-action--delete';
      case 'sync_fingerprint':
      case 'bulk_sync': return 'wal-action wal-action--sync';
      default: return 'wal-action wal-action--default';
    }
  };

  const formatTimestamp = (timestamp: Date) =>
    new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedAction('all');
    setSelectedResource('all');
    setSelectedSuccess('all');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  return (
    <div className="wal-page">
      <div className="wal-toolbar">
        <input
          type="text"
          className="wal-search"
          placeholder="Search action, user…"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <select className="wal-select" value={selectedResource} onChange={(e) => { setSelectedResource(e.target.value); setCurrentPage(1); }}>
          {RESOURCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select className="wal-select" value={selectedAction} onChange={(e) => { setSelectedAction(e.target.value); setCurrentPage(1); }}>
          {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select className="wal-select" value={selectedSuccess} onChange={(e) => { setSelectedSuccess(e.target.value); setCurrentPage(1); }}>
          <option value="all">All results</option>
          <option value="true">Success</option>
          <option value="false">Failed</option>
        </select>
        <input type="date" className="wal-date" value={dateRange.start} onChange={(e) => { setDateRange((p) => ({ ...p, start: e.target.value })); setCurrentPage(1); }} />
        <input type="date" className="wal-date" value={dateRange.end} onChange={(e) => { setDateRange((p) => ({ ...p, end: e.target.value })); setCurrentPage(1); }} />
        <span className="wal-toolbar-note">{totalCount} events</span>
        <div className="wal-toolbar-actions">
          <button type="button" className="wal-btn" onClick={clearFilters}>Clear</button>
          <button type="button" className="wal-btn" onClick={fetchLogs}>
            <RefreshCw size={14} className={loading ? 'wal-spin' : ''} />
          </button>
          <button type="button" className="wal-btn wal-btn--primary" onClick={handleExport} disabled={exporting}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className="wal-toast wal-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="wal-table-panel">
        {loading && logs.length === 0 ? (
          <div className="wal-loading">
            <RefreshCw size={20} className="wal-spin" />
            Loading audit logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="wal-empty">
            <AlertTriangle size={24} strokeWidth={1.5} />
            <p>No audit logs match your filters</p>
            <button type="button" className="wal-btn" onClick={clearFilters}>Reset filters</button>
          </div>
        ) : (
          <>
            <div className="wal-table-scroll">
              <table className="wal-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Resource</th>
                    <th>User</th>
                    <th>Time</th>
                    <th>Result</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td><span className={getActionClass(log.action)}>{log.action}</span></td>
                      <td>
                        <span className="wal-resource">{log.resource}</span>
                        {log.resourceId && <span className="wal-resource-id">#{log.resourceId.slice(0, 10)}…</span>}
                      </td>
                      <td>
                        <span className="wal-user">{log.user}</span>
                        {log.userIp && <span className="wal-ip">{log.userIp}</span>}
                      </td>
                      <td><span className="wal-time">{formatTimestamp(log.timestamp)}</span></td>
                      <td>
                        <span className={`wal-badge ${log.success ? 'wal-badge--ok' : 'wal-badge--fail'}`}>
                          {log.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="wal-icon-btn" title="View details" onClick={() => setSelectedLog(log)}>
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="wal-pagination">
                <span>Page {currentPage} of {totalPages}</span>
                <div className="wal-pagination-btns">
                  <button type="button" className="wal-btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button type="button" className="wal-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedLog && (
        <div className="wal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="wal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wal-modal-header">
              <div>
                <h3 className="wal-modal-title">Audit log details</h3>
                <p className="wal-modal-sub">{selectedLog._id}</p>
              </div>
              <button type="button" className="wal-modal-close" onClick={() => setSelectedLog(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="wal-modal-body">
              <div className="wal-detail-grid">
                <div className="wal-detail-item"><label>Action</label><span className={getActionClass(selectedLog.action)}>{selectedLog.action}</span></div>
                <div className="wal-detail-item"><label>Result</label><span className={`wal-badge ${selectedLog.success ? 'wal-badge--ok' : 'wal-badge--fail'}`}>{selectedLog.success ? 'Success' : 'Failed'}</span></div>
                <div className="wal-detail-item"><label>Resource</label><span>{selectedLog.resource}</span></div>
                <div className="wal-detail-item"><label>Time</label><span>{formatTimestamp(selectedLog.timestamp)}</span></div>
                <div className="wal-detail-item"><label>User</label><span>{selectedLog.user}</span></div>
                <div className="wal-detail-item"><label>IP</label><span>{selectedLog.userIp || '—'}</span></div>
              </div>
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="wal-json"><pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre></div>
              )}
              {selectedLog.oldValue != null && (
                <div className="wal-json"><strong>Previous:</strong><pre>{JSON.stringify(selectedLog.oldValue, null, 2)}</pre></div>
              )}
              {selectedLog.newValue != null && (
                <div className="wal-json"><strong>New:</strong><pre>{JSON.stringify(selectedLog.newValue, null, 2)}</pre></div>
              )}
              {selectedLog.errorMessage && (
                <div className="wal-toast wal-toast--err" style={{ marginTop: 12 }}>{selectedLog.errorMessage}</div>
              )}
            </div>
            <div className="wal-modal-footer">
              <button type="button" className="wal-btn" onClick={() => setSelectedLog(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogViewer;
